import React, { useState, useEffect } from "react";
import { api } from "../../lib/api";
import { X, Mail } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../context/I18nContext";
import { useInstallSuppress } from "../../lib/pwaInstall";

export default function EmailVerify({ open, email, onClose, onVerified }) {
  const { t } = useI18n();
  useInstallSuppress(open);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [sent, setSent] = useState(false);

  useEffect(() => { if (!cooldown) return; const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000); return () => clearInterval(t); }, [cooldown]);

  const send = async (auto = false) => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/email/send-code");
      if (data.already_verified) { onVerified(); return; }
      setSent(true); setCooldown(30);
      toast.success(t("ev.code_sent", "Kod je poslat na email"));
    } catch (e) { toast.error(e?.response?.data?.detail || t("ev.send_err", "Greška pri slanju koda")); }
    finally { setBusy(false); }
  };

  // Auto-send the code once when the screen opens (verification is one-time; backend rate-limits).
  useEffect(() => { if (open && !sent) send(true); /* eslint-disable-next-line */ }, [open]);

  if (!open) return null;

  const verify = async () => {
    if (code.trim().length < 4) { toast.error(t("ev.enter_code", "Unesite kod iz email-a")); return; }
    setBusy(true);
    try { await api.post("/auth/email/verify-code", { code }); toast.success(t("ev.verified", "Email verifikovan")); onVerified(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("ev.wrong", "Kod nije ispravan. Pokušajte ponovo.")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] max-w-md mx-auto flex items-end" data-testid="email-verify-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full bg-white rounded-t-3xl p-6 animate-slide-up">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-black text-xl flex items-center gap-2"><Mail size={18} /> {t("ev.title", "Verifikacija email-a")}</h2>
          <button data-testid="ev-close" onClick={onClose} className="bg-slate-100 rounded-full p-2"><X size={18} /></button>
        </div>
        <p className="text-sm text-slate-500 mb-3">{t("ev.sent_to", "Poslali smo kod na")} <b>{email}</b>. {t("ev.enter", "Unesite ga da biste mogli da poručite.")}</p>
        <input data-testid="ev-code-input" inputMode="numeric" value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="______"
          className="w-full text-center tracking-[0.5em] text-2xl font-display font-black px-4 py-3 rounded-2xl bg-slate-100 outline-none focus:ring-2 focus:ring-brand-red" />
        <button data-testid="ev-verify-code" disabled={busy} onClick={verify}
          className="mt-4 w-full bg-brand-red text-white rounded-full py-3.5 font-display font-bold disabled:opacity-50">
          {busy ? t("ev.checking", "Provera…") : t("ev.confirm", "Potvrdi")}
        </button>
        <button data-testid="ev-resend" disabled={cooldown > 0 || busy} onClick={() => send(false)}
          className="mt-2 w-full text-sm font-semibold text-slate-500 disabled:opacity-50">
          {cooldown > 0 ? `${t("ev.resend_in", "Pošalji ponovo za")} ${cooldown} s` : t("ev.resend", "Pošalji kod ponovo")}
        </button>
      </div>
    </div>
  );
}
